import React, { Component } from "react";
import { connect } from "react-redux";
import { Alert, Button, Form, Input, Modal, notification, Radio } from "antd";
import styled from "styled-components";
import { loginUser } from "redux/auth";
import {
  UserOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  ArrowLeftOutlined,
  ReloadOutlined,
  NumberOutlined,
} from "@ant-design/icons";
import requestAuth from "./../../utils/requestAuth";
import i18n from "./../../i18n";
import bcrypt from "bcryptjs";
// TODO: подтверди путь и реализацию (см. блок в чате). Бэкенд той же линейки, что Караганда.
import {
  passwordRecoveryRequest,
  passwordRecoveryVerify,
  passwordRecoveryReset,
} from "../../services/api";

// ===== helpers для телефона (KZ) =====
// оставляем только цифры и приводим к 7XXXXXXXXXX (11 цифр) — для отправки на бэк
const phoneDigits = (value = "") => {
  let d = (value || "").replace(/\D/g, "");
  if (d.startsWith("8")) d = "7" + d.slice(1);
  if (d && !d.startsWith("7")) d = "7" + d;
  return d.slice(0, 11);
};

// маска для отображения: +7 (XXX) XXX-XX-XX — используется в Form.Item normalize
const formatKzPhone = (value) => {
  if (!value) return "";
  const d = phoneDigits(value);
  const p2 = d.slice(1, 4);
  const p3 = d.slice(4, 7);
  const p4 = d.slice(7, 9);
  const p5 = d.slice(9, 11);
  let out = "+7";
  if (p2) out += ` (${p2}`;
  if (p2.length === 3) out += ")";
  if (p3) out += ` ${p3}`;
  if (p4) out += `-${p4}`;
  if (p5) out += `-${p5}`;
  return out;
};

class Login extends Component {
  // реф на форму восстановления (аналог form-объекта из Form.create в antd v3)
  recoveryFormRef = React.createRef();
  countdownTimer = null;

  state = {
    sent: false,
    crypted: [],

    // восстановление пароля
    recoveryOpen: false,
    recoveryType: "EMAIL", // "EMAIL" | "SMS"
    requestLoading: false,
    verifyLoading: false,
    saveLoading: false,
    serverToken: "",
    secondsLeft: 0,
    requestSuccess: false,
  };

  componentWillUnmount() {
    this.clearCountdown();
  }

  // ===== авторизация (без изменений) =====
  handleSubmit = (e) => {
    this.props
      .loginUser(e)
      .catch(() => {
        this.setState({ sent: false });
        notification.error({
          key: "requestFailedNotification",
          message: "Войти не удалось",
          description: "Вы ввели неправильный логин или пароль.",
        });
      })
      .then((response) => {
        if (response) {
          requestAuth("/users/current").then((r) => {
            requestAuth("/get/casesroles/" + r.roleEntity.id).then((data) => {
              let crypted = [];
              const casesRoles = data.map((e) => "/" + e.casesEntity.code);
              casesRoles.push("/");
              casesRoles.forEach((caseItem) => {
                const hash = bcrypt.hashSync(caseItem, 2);
                crypted.push(hash);
              });
              localStorage.setItem("modules", JSON.stringify(crypted));
            });
          });
        }
      });
    this.setState({ sent: true });
  };

  // ===== таймер 60 сек =====
  startCountdown = (seconds) => {
    this.clearCountdown();
    this.setState({ secondsLeft: seconds });
    this.countdownTimer = setInterval(() => {
      this.setState((prev) => {
        if (prev.secondsLeft <= 1) {
          this.clearCountdown();
          return { secondsLeft: 0 };
        }
        return { secondsLeft: prev.secondsLeft - 1 };
      });
    }, 1000);
  };

  clearCountdown = () => {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  };

  // ===== переключение режима восстановления =====
  resetRecovery = () => {
    this.clearCountdown();
    this.setState({ serverToken: "", secondsLeft: 0, requestSuccess: false });
    const form = this.recoveryFormRef.current;
    if (form) {
      form.resetFields([
        "recoveryEmail",
        "recoveryPhone",
        "recoveryCode",
        "newPassword",
        "newPasswordConfirm",
      ]);
    }
  };

  openRecovery = () => {
    this.setState({ recoveryOpen: true, recoveryType: "EMAIL" });
    this.resetRecovery();
  };

  closeRecovery = () => {
    this.setState({ recoveryOpen: false });
    this.resetRecovery();
  };

  // при смене канала сбрасываем токен/таймер, чтобы код запрашивался заново
  handleChangeRecoveryType = (e) => {
    this.setState({ recoveryType: e.target.value });
    this.resetRecovery();
  };

  // ===== шаг 1: запрос кода (email или sms) =====
  handleGetCode = () => {
    const form = this.recoveryFormRef.current;
    const { recoveryType } = this.state;
    const identityField =
      recoveryType === "SMS" ? "recoveryPhone" : "recoveryEmail";

    form
      .validateFields([identityField])
      .then(async (values) => {
        this.setState({ requestLoading: true });
        try {
          // payload по контракту: { type, email? , phone? }
          const payload = { type: recoveryType };
          if (recoveryType === "SMS") {
            payload.phone = phoneDigits(values.recoveryPhone); // 7XXXXXXXXXX
          } else {
            payload.email = (values.recoveryEmail || "").trim();
          }

          const res = await passwordRecoveryRequest(payload);
          if (!res || !res.token) {
            notification.error({
              message: "Не удалось отправить код",
              description: "Сервер не вернул токен подтверждения.",
            });
            return;
          }
          // serverToken — это идентификатор попытки, НЕ код из письма/SMS
          this.setState({ serverToken: res.token, requestSuccess: true });
          this.startCountdown(60);
          notification.success({
            message: "Код отправлен",
            description:
              recoveryType === "SMS"
                ? "На ваш телефон был отправлен код подтверждения."
                : "На вашу почту был отправлен код подтверждения.",
          });
        } catch (e) {
          notification.error({
            message: "Ошибка отправки кода",
            description:
              (e && e.message) ||
              (recoveryType === "SMS"
                ? "По указанному номеру нет зарегистрированного пользователя"
                : "По указанной электронной почте нет зарегистрированного пользователя"),
          });
        } finally {
          this.setState({ requestLoading: false });
        }
      })
      .catch(() => {
        // ошибки валидации уже подсвечены на поле
      });
  };

  // ===== шаг 2: проверка кода (отдельным запросом, до сброса) =====
  verifyCode = async () => {
    const form = this.recoveryFormRef.current;
    const code = form.getFieldValue("recoveryCode");
    const { serverToken } = this.state;
    if (!serverToken || !code) return false;
    this.setState({ verifyLoading: true });
    try {
      await passwordRecoveryVerify({ token: serverToken, code });
      return true;
    } catch (e) {
      form.setFields([{ name: "recoveryCode", errors: ["Неверный код"] }]);
      notification.error({
        message: "Неверный код подтверждения",
        description:
          (e && e.message) || "Проверьте введённый код и попробуйте ещё раз.",
      });
      return false;
    } finally {
      this.setState({ verifyLoading: false });
    }
  };

  // ===== шаг 3: сохранить новый пароль =====
  handleSaveNewPassword = () => {
    const form = this.recoveryFormRef.current;
    const { recoveryType } = this.state;
    const identityField =
      recoveryType === "SMS" ? "recoveryPhone" : "recoveryEmail";

    form
      .validateFields([
        identityField,
        "recoveryCode",
        "newPassword",
        "newPasswordConfirm",
      ])
      .then(async (values) => {
        const p1 = values.newPassword;
        const p2 = values.newPasswordConfirm;
        if (p1 !== p2) {
          const msg =
            "Не совпадают пароли / Parólder sáıkes kelmeıdi / Passwords don't match";
          form.setFields([
            { name: "newPassword", errors: [msg] },
            { name: "newPasswordConfirm", errors: [msg] },
          ]);
          return;
        }

        const ok = await this.verifyCode();
        if (!ok) return;

        this.setState({ saveLoading: true });
        try {
          await passwordRecoveryReset({
            token: this.state.serverToken,
            newPassword: p1,
          });
          notification.success({
            title: "Пароль изменён",
            content: "Вы можете войти, используя новый пароль.",
            onOk: () => window.location.replace("/login"),
          });
          this.setState({ recoveryOpen: false });
          this.resetRecovery();
        } catch (e) {
          notification.error({
            message: "Ошибка сохранения",
            description:
              (e && e.message) ||
              "Не удалось сбросить пароль. Попробуйте позже.",
          });
        } finally {
          this.setState({ saveLoading: false });
        }
      })
      .catch(() => {});
  };

  render() {
    const {
      recoveryOpen,
      recoveryType,
      requestLoading,
      verifyLoading,
      saveLoading,
      serverToken,
      secondsLeft,
      requestSuccess,
    } = this.state;
    const isCounting = secondsLeft > 0;
    const isSms = recoveryType === "SMS";

    return (
      <Wrapper>
        <Container>
          {/* иконка и название — не трогаем */}
          <Img src="/img/new_logo.png" />
          <Headline>{i18n.t("loginPage.information")}</Headline>

          <FormContainer>
            <FormTitle>
              {recoveryOpen ? "Восстановление пароля" : "Авторизация"}
            </FormTitle>

            {/* ===== ФОРМА ВХОДА ===== */}
            {!recoveryOpen && (
              <Form onFinish={this.handleSubmit} className="login-form">
                <Form.Item
                  name="login"
                  rules={[
                    {
                      required: true,
                      message: "Пожалуйста, укажите имя пользователя.",
                    },
                  ]}
                >
                  <Input
                    size="large"
                    prefix={
                      <UserOutlined style={{ color: "rgba(0,0,0,.25)" }} />
                    }
                    placeholder="Логин"
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  rules={[
                    { required: true, message: "Пожалуйста, укажите пароль." },
                  ]}
                >
                  <Input
                    size="large"
                    prefix={
                      <LockOutlined style={{ color: "rgba(0,0,0,.25)" }} />
                    }
                    placeholder="Не менее 8 символов"
                    type="password"
                  />
                </Form.Item>

                <Button
                  type="primary"
                  htmlType="submit"
                  className="login-form-button"
                  size="large"
                  block
                  loading={this.state.sent}
                >
                  Войти
                </Button>

                <div style={{ marginTop: 12, textAlign: "center" }}>
                  <Button
                    type="link"
                    htmlType="button"
                    onClick={this.openRecovery}
                  >
                    Забыли пароль?
                  </Button>
                </div>
              </Form>
            )}

            {/* ===== ФОРМА ВОССТАНОВЛЕНИЯ ===== */}
            {recoveryOpen && (
              <Form ref={this.recoveryFormRef} className="login-form">
                <Button
                  type="primary"
                  htmlType="button"
                  style={{ marginBottom: 16 }}
                  onClick={this.closeRecovery}
                >
                  <ArrowLeftOutlined style={{ marginRight: 8 }} />
                  Назад к входу
                </Button>

                {/* выбор канала: Email / SMS */}
                <div style={{ marginBottom: 16 }}>
                  <Radio.Group
                    value={recoveryType}
                    onChange={this.handleChangeRecoveryType}
                    optionType="button"
                    buttonStyle="solid"
                    style={{ display: "flex", width: "100%" }}
                  >
                    <Radio.Button
                      value="EMAIL"
                      style={{ flex: 1, textAlign: "center" }}
                    >
                      <MailOutlined style={{ marginRight: 6 }} />
                      По Email
                    </Radio.Button>
                    <Radio.Button
                      value="SMS"
                      style={{ flex: 1, textAlign: "center" }}
                    >
                      <PhoneOutlined style={{ marginRight: 6 }} />
                      По SMS
                    </Radio.Button>
                  </Radio.Group>
                </div>

                {/* поле идентификатора: email или телефон */}
                {isSms ? (
                  <Form.Item
                    name="recoveryPhone"
                    normalize={formatKzPhone}
                    rules={[
                      {
                        required: true,
                        message: "Данное поле должно быть заполнено",
                      },
                      {
                        validator: (_, value) => {
                          if (!value) return Promise.resolve();
                          return phoneDigits(value).length === 11
                            ? Promise.resolve()
                            : Promise.reject(
                                new Error("Введите корректный номер телефона"),
                              );
                        },
                      },
                    ]}
                  >
                    <Input
                      size="large"
                      inputMode="tel"
                      placeholder="+7 (___) ___-__-__"
                      prefix={
                        <PhoneOutlined style={{ color: "rgba(0,0,0,.25)" }} />
                      }
                    />
                  </Form.Item>
                ) : (
                  <Form.Item
                    name="recoveryEmail"
                    rules={[
                      {
                        required: true,
                        message: "Данное поле должно быть заполнено",
                      },
                      { type: "email", message: "Некорректный e-mail" },
                    ]}
                  >
                    <Input
                      size="large"
                      placeholder="Email для кода подтверждения"
                      prefix={
                        <MailOutlined style={{ color: "rgba(0,0,0,.25)" }} />
                      }
                    />
                  </Form.Item>
                )}

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 12,
                  }}
                >
                  <Button
                    htmlType="button"
                    onClick={this.handleGetCode}
                    disabled={isCounting || requestLoading}
                    loading={requestLoading}
                  >
                    {isCounting
                      ? "Код отправлен"
                      : "Получить код подтверждения"}
                  </Button>

                  {isCounting ? (
                    <span style={{ color: "#595959" }}>
                      Введите код за {secondsLeft} сек
                    </span>
                  ) : (
                    <Button htmlType="button" onClick={this.handleGetCode}>
                      <ReloadOutlined />
                      Отправить код повторно
                    </Button>
                  )}
                </div>

                {requestSuccess && (
                  <Alert
                    type="success"
                    showIcon
                    message={
                      isSms
                        ? "На ваш телефон был отправлен код подтверждения."
                        : "На вашу почту был отправлен код подтверждения."
                    }
                    style={{ marginBottom: 16 }}
                  />
                )}

                {serverToken && (
                  <>
                    <Form.Item
                      label={
                        isSms ? "Введите код из SMS" : "Введите код из письма"
                      }
                      name="recoveryCode"
                      rules={[
                        {
                          required: true,
                          message: "Данное поле должно быть заполнено",
                        },
                        { len: 4, message: "Код состоит из 4 цифр" },
                      ]}
                    >
                      <Input
                        size="large"
                        maxLength={4}
                        placeholder="4-значный код"
                        prefix={
                          <NumberOutlined
                            style={{ color: "rgba(0,0,0,.25)" }}
                          />
                        }
                      />
                    </Form.Item>

                    <Form.Item
                      label="Новый пароль"
                      name="newPassword"
                      rules={[
                        {
                          required: true,
                          message: "Данное поле должно быть заполнено",
                        },
                      ]}
                    >
                      <Input
                        type="password"
                        size="large"
                        placeholder="Не менее 8 символов"
                        prefix={
                          <LockOutlined style={{ color: "rgba(0,0,0,.25)" }} />
                        }
                      />
                    </Form.Item>

                    <Form.Item
                      label="Повторите пароль"
                      name="newPasswordConfirm"
                      dependencies={["newPassword"]}
                      rules={[
                        {
                          required: true,
                          message: "Данное поле должно быть заполнено",
                        },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (
                              !value ||
                              getFieldValue("newPassword") === value
                            ) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              new Error(
                                "Не совпадают пароли / Parólder sáıkes kelmeıdi / Passwords don't match",
                              ),
                            );
                          },
                        }),
                      ]}
                    >
                      <Input
                        type="password"
                        size="large"
                        placeholder="Повторите пароль"
                        prefix={
                          <LockOutlined style={{ color: "rgba(0,0,0,.25)" }} />
                        }
                      />
                    </Form.Item>

                    <Button
                      type="primary"
                      htmlType="button"
                      block
                      onClick={this.handleSaveNewPassword}
                      loading={verifyLoading || saveLoading}
                    >
                      Сохранить
                    </Button>
                  </>
                )}
              </Form>
            )}
          </FormContainer>
        </Container>
      </Wrapper>
    );
  }
}

export default connect(null, { loginUser })(Login);

let Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 100%;
`;

let Container = styled.div`
  max-width: 540px;
  padding: 100px 0 200px;
`;

let Headline = styled.div`
  font-style: normal;
  font-weight: normal;
  font-size: 24px;
  line-height: 32px;
  text-align: center;
  color: #ffffff;
  margin: 30px 0;
`;

let Img = styled.img`
  width: 74px;
`;

let FormContainer = styled.div`
  background: #ffffff;
  border-radius: 8px;
  padding: 20px 30px;
  text-align: left;
`;

let FormTitle = styled.div`
  font-size: 24px;
  line-height: 32px;
  text-align: center;
  color: #24262f;
  margin-bottom: 20px;
`;
